import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { supabase, messagesService } from '../services/supabaseService';

// ============================================================
// MessagesInbox — Skrzynka odbiorcza z wątkami konwersacji
// ============================================================

const MessagesInbox = ({ open, onClose, userEmail, userId, onUnreadChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [messages, setMessages] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuThread, setMenuThread] = useState(null);
  const [avatarMap, setAvatarMap] = useState({});

  // ============================================================
  // Klucz wątku — grupuj wg drugiej osoby + ogłoszenie
  // ============================================================
  const getThreadKey = useCallback((msg) => {
    const otherEmail = msg._type === 'received' ? msg.sender_email : msg.recipient_email;
    const listingId = msg.property_id || msg.tenant_profile_id || 'general';
    return `${otherEmail}::${listingId}`;
  }, []);

  // ============================================================
  // Grupuj wiadomości w wątki
  // ============================================================
  const groupIntoThreads = useCallback((allMessages) => {
    const threadMap = {};

    allMessages.forEach(msg => {
      const key = getThreadKey(msg);
      if (!threadMap[key]) {
        const otherEmail = msg._type === 'received' ? msg.sender_email : msg.recipient_email;
        const otherName = msg._type === 'received'
          ? (msg.sender_name || msg.sender_email || 'Użytkownik')
          : (msg.recipient_name || msg.recipient_email || 'Użytkownik');

        threadMap[key] = {
          key,
          otherEmail,
          otherName,
          listingTitle: msg.listing_title,
          propertyId: msg.property_id,
          tenantProfileId: msg.tenant_profile_id,
          messages: [],
          unreadCount: 0,
          lastMessage: null,
          lastDate: null
        };
      }

      threadMap[key].messages.push(msg);

      if (msg._type === 'received' && !msg.is_read) {
        threadMap[key].unreadCount++;
      }

      const msgDate = new Date(msg.created_at);
      if (!threadMap[key].lastDate || msgDate > threadMap[key].lastDate) {
        threadMap[key].lastDate = msgDate;
        threadMap[key].lastMessage = msg;
      }

      if (msg._type === 'received' && msg.sender_name && msg.sender_name.trim()) {
        threadMap[key].otherName = msg.sender_name;
      } else if (msg._type === 'sent' && msg.recipient_name && msg.recipient_name.trim()) {
        threadMap[key].otherName = msg.recipient_name;
      }
    });

    // Sortuj wiadomości w wątku chronologicznie (najstarsza → najnowsza)
    Object.values(threadMap).forEach(thread => {
      thread.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });

    // Sortuj wątki wg daty ostatniej wiadomości (najnowsze na górze)
    return Object.values(threadMap).sort((a, b) => b.lastDate - a.lastDate);
  }, [getThreadKey]);

  // ============================================================
  // Pobierz wiadomości
  // ============================================================
  const fetchMessages = useCallback(async () => {
    if (!userEmail || !open) return;
    setLoading(true);
    try {
      const { data: received, error: recvErr } = await supabase
        .from('messages')
        .select('*')
        .eq('recipient_email', userEmail)
        .eq('is_deleted_by_recipient', false)
        .order('created_at', { ascending: false });
      if (recvErr) throw recvErr;

      const { data: sent, error: sentErr } = await supabase
        .from('messages')
        .select('*')
        .eq('sender_user_id', userId)
        .eq('is_deleted_by_sender', false)
        .order('created_at', { ascending: false });
      if (sentErr) throw sentErr;

      const allMessages = [
        ...(received || []).map(m => ({ ...m, _type: 'received' })),
        ...(sent || []).map(m => ({ ...m, _type: 'sent' }))
      ];

      setMessages(allMessages);
      const grouped = groupIntoThreads(allMessages);
      setThreads(grouped);

      // Pobierz avatary dla uczestników wątków
      const emails = [...new Set(grouped.map(t => t.otherEmail).filter(Boolean))];
      if (emails.length > 0) {
        try {
          const { data: usersData } = await supabase
            .from('users')
            .select('email, avatar_url')
            .in('email', emails);
          const { data: tenantsData } = await supabase
            .from('tenant_profiles')
            .select('email, profile_photo_url')
            .in('email', emails);
          const newAvatarMap = {};
          (usersData || []).forEach(u => {
            if (u.avatar_url) newAvatarMap[u.email] = u.avatar_url;
          });
          // Tenant profile photo jako fallback (nie nadpisuj Google avatar)
          (tenantsData || []).forEach(t => {
            if (t.profile_photo_url && !newAvatarMap[t.email]) {
              newAvatarMap[t.email] = t.profile_photo_url;
            }
          });
          setAvatarMap(prev => ({ ...prev, ...newAvatarMap }));
        } catch (err) {
          console.error('Błąd pobierania avatarów:', err);
        }
      }

      // Aktualizuj selectedThread jeśli otwarty
      setSelectedThread(prev => {
        if (!prev) return prev;
        const updated = grouped.find(t => t.key === prev.key);
        return updated || prev;
      });
    } catch (err) {
      console.error('Błąd pobierania wiadomości:', err);
    } finally {
      setLoading(false);
    }
  }, [userEmail, userId, open, groupIntoThreads]);

  useEffect(() => {
    if (open) {
      fetchMessages();
      setSelectedThread(null);
      setReplyText('');
      // Zapisz kiedy user ostatnio otworzył skrzynkę — używane do pomijania powiadomień email
      if (userId) {
        supabase
          .from('notification_preferences')
          .upsert(
            { user_id: userId, last_inbox_viewed_at: new Date().toISOString() },
            { onConflict: 'user_id' }
          )
          .then(({ error }) => { if (error) console.warn('last_inbox_viewed_at upsert error:', error.message); });
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Otwórz wątek — oznacz wszystkie nieprzeczytane
  // ============================================================
  const handleOpenThread = async (thread) => {
    setSelectedThread(thread);
    setReplyText('');

    const unreadMsgs = thread.messages.filter(m => m._type === 'received' && !m.is_read);
    if (unreadMsgs.length > 0) {
      try {
        const ids = unreadMsgs.map(m => m.id);
        await supabase
          .from('messages')
          .update({ is_read: true })
          .in('id', ids);

        setMessages(prev =>
          prev.map(m => ids.includes(m.id) ? { ...m, is_read: true } : m)
        );
        setThreads(prev =>
          prev.map(t => t.key === thread.key
            ? { ...t, unreadCount: 0, messages: t.messages.map(m => ({ ...m, is_read: true })) }
            : t
          )
        );
        setSelectedThread(prev => prev ? {
          ...prev, unreadCount: 0,
          messages: prev.messages.map(m => ({ ...m, is_read: true }))
        } : prev);
        if (onUnreadChange) onUnreadChange();
      } catch (err) {
        console.error('Błąd oznaczania:', err);
      }
    }
  };

  // ============================================================
  // Wyślij odpowiedź w wątku
  // ============================================================
  const handleReply = async () => {
    if (!replyText.trim() || replySending || !selectedThread) return;
    setReplySending(true);
    try {
      await messagesService.send({
        propertyId: selectedThread.propertyId,
        tenantProfileId: selectedThread.tenantProfileId,
        recipientEmail: selectedThread.otherEmail,
        recipientName: selectedThread.otherName,
        senderUserId: userId,
        senderName: '',
        senderEmail: userEmail,
        messageText: replyText.trim(),
        listingTitle: selectedThread.listingTitle
      });
      setReplyText('');
      await fetchMessages();
    } catch (err) {
      console.error('Błąd wysyłania:', err);
      alert('Nie udało się wysłać wiadomości.');
    } finally {
      setReplySending(false);
    }
  };

  // ============================================================
  // Usuń cały wątek (soft delete)
  // ============================================================
  const handleDeleteThread = async (thread) => {
    try {
      for (const msg of thread.messages) {
        const field = msg._type === 'received' ? 'is_deleted_by_recipient' : 'is_deleted_by_sender';
        await supabase
          .from('messages')
          .update({ [field]: true })
          .eq('id', msg.id);
      }
      setThreads(prev => prev.filter(t => t.key !== thread.key));
      const threadMsgIds = thread.messages.map(m => m.id);
      setMessages(prev => prev.filter(m => !threadMsgIds.includes(m.id)));
      if (selectedThread?.key === thread.key) setSelectedThread(null);
      if (onUnreadChange) onUnreadChange();
    } catch (err) {
      console.error('Błąd usuwania:', err);
    }
    setMenuAnchor(null);
  };

  // ============================================================
  // Zablokuj użytkownika
  // ============================================================
  const handleBlockUser = async (thread) => {
    if (!thread.otherEmail) return;
    try {
      const { data } = await supabase
        .from('users')
        .select('blocked_users')
        .eq('id', userId)
        .single();
      const blockedList = data?.blocked_users || [];
      if (!blockedList.includes(thread.otherEmail)) {
        await supabase
          .from('users')
          .update({ blocked_users: [...blockedList, thread.otherEmail] })
          .eq('id', userId);
      }
      setThreads(prev => prev.filter(t => t.otherEmail !== thread.otherEmail));
      setSelectedThread(null);
      alert(`Użytkownik ${thread.otherEmail} został zablokowany.`);
    } catch (err) {
      console.error('Błąd blokowania:', err);
    }
    setMenuAnchor(null);
  };

  // ============================================================
  // Helpers
  // ============================================================
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Przed chwilą';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min temu`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} godz. temu`;
    if (diff < 172800000) return 'Wczoraj';
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name, email) => {
    if (name && name.trim()) return name.trim().charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  };

  const totalUnread = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  // ============================================================
  // WIDOK WĄTKU — pełna konwersacja (styl chat)
  // ============================================================
  const renderThreadDetail = () => {
    if (!selectedThread) return null;

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 2, borderBottom: '1px solid #E5E7EB'
        }}>
          <IconButton onClick={() => setSelectedThread(null)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Avatar src={avatarMap[selectedThread.otherEmail] || ''} sx={{ bgcolor: '#D2691E', width: 36, height: 36, fontSize: '14px' }}>
            {getInitials(selectedThread.otherName, selectedThread.otherEmail)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }} noWrap>
              {selectedThread.otherName}
            </Typography>
            {selectedThread.listingTitle && (
              <Typography variant="caption" sx={{ color: '#D2691E', display: 'block' }} noWrap>
                {selectedThread.listingTitle}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              setMenuAnchor(e.currentTarget);
              setMenuThread(selectedThread);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Wiadomości — chat bubbles */}
        <Box sx={{
          flex: 1, overflow: 'auto', p: 2,
          display: 'flex', flexDirection: 'column', gap: 1.5,
          bgcolor: '#FAFAFA'
        }}>
          {selectedThread.messages.map((msg, idx) => {
            const isSent = msg._type === 'sent';
            const prevMsg = idx > 0 ? selectedThread.messages[idx - 1] : null;
            const msgDate = new Date(msg.created_at).toLocaleDateString('pl-PL');
            const prevDate = prevMsg ? new Date(prevMsg.created_at).toLocaleDateString('pl-PL') : null;
            const showDateSep = msgDate !== prevDate;

            return (
              <React.Fragment key={msg.id}>
                {showDateSep && (
                  <Box sx={{ textAlign: 'center', my: 1 }}>
                    <Chip
                      label={msgDate === new Date().toLocaleDateString('pl-PL') ? 'Dzisiaj' : msgDate}
                      size="small"
                      sx={{ fontSize: '10px', height: 20, bgcolor: '#E5E7EB', color: '#6B7280' }}
                    />
                  </Box>
                )}
                <Box sx={{
                  display: 'flex',
                  justifyContent: isSent ? 'flex-end' : 'flex-start',
                  px: 0.5
                }}>
                  <Box sx={{
                    maxWidth: '80%',
                    p: 1.5,
                    borderRadius: 2.5,
                    bgcolor: isSent ? '#D2691E' : '#FFFFFF',
                    color: isSent ? '#fff' : '#1F2937',
                    boxShadow: isSent ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
                    borderBottomRightRadius: isSent ? 4 : 20,
                    borderBottomLeftRadius: isSent ? 20 : 4,
                  }}>
                    <Typography variant="body2" sx={{
                      whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '14px'
                    }}>
                      {msg.message}
                    </Typography>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3, mt: 0.5
                    }}>
                      <Typography variant="caption" sx={{
                        color: isSent ? 'rgba(255,255,255,0.7)' : '#9CA3AF',
                        fontSize: '10px'
                      }}>
                        {formatTime(msg.created_at)}
                      </Typography>
                      {isSent && (
                        msg.is_read
                          ? <DoneAllIcon sx={{ fontSize: 14, color: '#90EE90' }} />
                          : <DoneIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>

        {/* Pole odpowiedzi */}
        <Box sx={{ p: 1.5, borderTop: '1px solid #E5E7EB', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Napisz wiadomość..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              disabled={replySending}
              multiline
              maxRows={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleReply();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '14px' }
              }}
            />
            <IconButton
              onClick={handleReply}
              disabled={!replyText.trim() || replySending}
              sx={{
                bgcolor: '#D2691E', color: '#fff',
                width: 40, height: 40,
                '&:hover': { bgcolor: '#B8591A' },
                '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' }
              }}
            >
              {replySending
                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                : <SendIcon sx={{ fontSize: 18 }} />
              }
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  // ============================================================
  // WIDOK LISTY WĄTKÓW
  // ============================================================
  const renderThreadList = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 2, borderBottom: '1px solid #E5E7EB'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon sx={{ color: '#D2691E' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Wiadomości
          </Typography>
          {totalUnread > 0 && (
            <Chip
              label={`${totalUnread} ${totalUnread === 1 ? 'nowa' : 'nowych'}`}
              size="small"
              sx={{
                bgcolor: '#EF4444', color: 'white',
                fontWeight: 600, fontSize: '11px', height: 22
              }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#D2691E' }} />
          </Box>
        ) : threads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <InboxIcon sx={{ fontSize: 64, color: '#E5E7EB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 1, fontWeight: 500 }}>
              Brak wiadomości
            </Typography>
            <Typography variant="body2" sx={{ color: '#D1D5DB' }}>
              Tutaj pojawią się wiadomości od zainteresowanych użytkowników.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {threads.map((thread, idx) => {
              const lastMsg = thread.lastMessage;
              const lastMsgPreview = lastMsg._type === 'sent'
                ? `Ty: ${lastMsg.message}`
                : lastMsg.message;
              const hasUnread = thread.unreadCount > 0;

              return (
                <React.Fragment key={thread.key}>
                  <ListItem
                    onClick={() => handleOpenThread(thread)}
                    sx={{
                      cursor: 'pointer', py: 1.5, px: 2,
                      bgcolor: hasUnread ? 'rgba(210, 105, 30, 0.04)' : 'transparent',
                      '&:hover': { bgcolor: '#F9FAFB' },
                      transition: 'background-color 0.15s'
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end" size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuAnchor(e.currentTarget);
                          setMenuThread(thread);
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18, color: '#999' }} />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={avatarMap[thread.otherEmail] || ''} sx={{ bgcolor: '#D2691E', width: 42, height: 42, fontSize: '16px' }}>
                        {getInitials(thread.otherName, thread.otherEmail)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: hasUnread ? 700 : 500, color: '#1F2937',
                            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {thread.otherName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', whiteSpace: 'nowrap', fontSize: '11px' }}>
                            {formatDate(lastMsg.created_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          {thread.listingTitle && (
                            <Typography variant="caption" sx={{ color: '#D2691E', display: 'block', fontSize: '11px', mb: 0.2 }}>
                              {thread.listingTitle}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{
                            color: hasUnread ? '#374151' : '#9CA3AF',
                            fontWeight: hasUnread ? 500 : 400,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            fontSize: '13px', maxWidth: isMobile ? '200px' : '350px'
                          }}>
                            {lastMsgPreview}
                          </Typography>
                          {thread.messages.length > 1 && (
                            <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '10px' }}>
                              {thread.messages.length} wiadomości
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {hasUnread && (
                      <Chip
                        label={thread.unreadCount === 1 ? 'Nowa' : thread.unreadCount}
                        size="small"
                        sx={{
                          bgcolor: '#22C55E', color: '#fff', fontWeight: 700,
                          fontSize: '10px', height: 20, minWidth: 20, mr: 1,
                          flexShrink: 0, '& .MuiChip-label': { px: 0.8 }
                        }}
                      />
                    )}
                  </ListItem>
                  {idx < threads.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '75vh',
            maxHeight: isMobile ? '100%' : '75vh',
            borderRadius: isMobile ? 0 : 3
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {selectedThread ? renderThreadDetail() : renderThreadList()}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 180, boxShadow: 3 } }}
      >
        <MenuItem onClick={() => { if (menuThread) handleDeleteThread(menuThread); }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon>
          <ListItemText sx={{ '& .MuiTypography-root': { color: '#EF4444' } }}>Usuń rozmowę</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuThread) handleBlockUser(menuThread); }}>
          <ListItemIcon><BlockIcon fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon>
          <ListItemText sx={{ '& .MuiTypography-root': { color: '#EF4444' } }}>Zablokuj użytkownika</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default MessagesInbox;

// ============================================================
// Hook do liczenia nieprzeczytanych — użyj w App.js
// ============================================================
export const useUnreadMessages = (userEmail, isLoggedIn) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userEmail || !isLoggedIn) {
      setUnreadCount(0);
      return;
    }

    const fetchCount = async () => {
      try {
        const count = await messagesService.countUnread(userEmail);
        setUnreadCount(count);
      } catch (err) {
        console.error('Błąd pobierania nieprzeczytanych:', err);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [userEmail, isLoggedIn]);

  return { unreadCount, setUnreadCount, refresh: async () => {
    if (!userEmail) return;
    try {
      const count = await messagesService.countUnread(userEmail);
      setUnreadCount(count);
    } catch {}
  }};
};
